import { observer } from "mobx-react-lite";
import { EasyAvatar } from "fin-web-components";
import { runInAction } from "mobx";
import { useCallback, useEffect, useRef, useState } from "react";
import { App, Button, Input, Modal, Radio } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import {
  CameraOutlined,
  CopyOutlined,
  ManOutlined,
  UserOutlined,
  WomanOutlined,
} from "@ant-design/icons";
import { H5MobileDatePicker } from "../components/common/H5MobileDatePicker";
import { globalModel } from "../global/GlobalModel";
import { UserProfileApi } from "./UserProfileApi";
import { randomNickname } from "./randomNickname";
import { USER_PROFILE_PAGE_LAYOUT } from "./Config";
import { avatarDisplayWord } from "../utils/avatarDisplayWord";
import { copyTextToClipboard } from "../utils/copyTextToClipboard";
import {
  resolveAppAvatarUrl,
  resolveAppMediaUrl,
  toPersistableAvatarUrl,
} from "../utils/resolveAppMediaUrl";
import "./ProfileCompleteModal.css";

const NICKNAME_MAX_LENGTH = USER_PROFILE_PAGE_LAYOUT.layout.nicknameMaxLength;
const BIRTHDAY_PANEL_DEFAULT = dayjs("1990-12-20");

export const ProfileCompleteModal = observer(() => {
  const { message: msgApi } = App.useApp();
  const forced = globalModel.profileSetupRequired;
  const optionalOpen = globalModel.profileEditorOpen;
  const open = forced || optionalOpen;
  const allowDismiss = !forced && optionalOpen;
  const [nickname, setNickname] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState<string | null>(null);
  const [gender, setGender] = useState<"U" | "M" | "F">("U");
  const [birthday, setBirthday] = useState<Dayjs | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);
  const uploadedServerAvatarRef = useRef<string | null>(null);

  const revokePreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setAvatarPreviewUrl(null);
  }, []);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!open) {
      revokePreview();
      return;
    }
    revokePreview();
    uploadedServerAvatarRef.current = null;
    if (forced) {
      setNickname(randomNickname().slice(0, NICKNAME_MAX_LENGTH));
      setAvatarUrl("");
      setGender("U");
      setBirthday(null);
      return;
    }
    if (optionalOpen && globalModel.currentUser) {
      const u = globalModel.currentUser;
      setNickname((u.name ?? "").trim() || "");
      setAvatarUrl(toPersistableAvatarUrl(u.avatarUrl));
      setGender((u.gender as "U" | "M" | "F") ?? "U");
      const b = u.birthday?.trim();
      if (b) {
        const d = dayjs(b);
        setBirthday(d.isValid() ? d : null);
      } else {
        setBirthday(null);
      }
    }
  }, [open, forced, optionalOpen, revokePreview]);

  const runPickFile = useCallback(
    async (file: File) => {
      const isImage =
        file.type.startsWith("image/") ||
        /\.(jpe?g|png|webp|heic|heif|gif|bmp)$/i.test(file.name);
      if (!isImage) {
        msgApi.warning("请选择图片");
        return;
      }
      revokePreview();
      const localUrl = URL.createObjectURL(file);
      previewUrlRef.current = localUrl;
      setAvatarPreviewUrl(localUrl);

      setUploading(true);
      try {
        const url = await UserProfileApi.uploadAvatarFromCompressedFile(file);
        if (!url) {
          revokePreview();
          uploadedServerAvatarRef.current = null;
          msgApi.error("上传失败，请重试");
          return;
        }
        uploadedServerAvatarRef.current = url;
        setAvatarUrl(toPersistableAvatarUrl(url));
        msgApi.success("已上传");
      } catch {
        revokePreview();
        uploadedServerAvatarRef.current = null;
        msgApi.error("上传失败，请重试");
      } finally {
        setUploading(false);
      }
    },
    [msgApi, revokePreview]
  );

  const letter = avatarDisplayWord(nickname) || "·";
  const displayAvatarUrl =
    avatarPreviewUrl ||
    resolveAppAvatarUrl(avatarUrl) ||
    resolveAppMediaUrl(avatarUrl) ||
    undefined;
  const userId = globalModel.currentUser?.id?.trim() ?? "";

  const onCopyUserId = useCallback(() => {
    if (!userId) {
      return;
    }
    void copyTextToClipboard(userId).then((ok) => {
      if (ok) {
        msgApi.success("已复制用户ID");
      } else {
        msgApi.error("复制失败，请重试");
      }
    });
  }, [msgApi, userId]);

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      e.target.value = "";
      if (f) {
        void runPickFile(f);
      }
    },
    [runPickFile]
  );

  const onSubmit = useCallback(async () => {
    const n = nickname.trim();
    if (!n) {
      msgApi.warning("请填写昵称");
      return;
    }
    if (n.length > NICKNAME_MAX_LENGTH) {
      msgApi.warning(`昵称最多 ${NICKNAME_MAX_LENGTH} 字`);
      return;
    }
    const rawAv = (uploadedServerAvatarRef.current ?? avatarUrl).trim();
    const persistAv = toPersistableAvatarUrl(rawAv);
    if (avatarPreviewUrl && !persistAv) {
      msgApi.warning("请等待头像上传完成");
      return;
    }
    if (persistAv.length > 1024) {
      msgApi.warning("头像信息异常，请重传");
      return;
    }
    if (birthday) {
      if (birthday.isAfter(dayjs(), "day")) {
        msgApi.warning("生日不能晚于今天");
        return;
      }
      if (birthday.isBefore(dayjs("1900-01-01"), "day")) {
        msgApi.warning("生日不早于 1900-01-01");
        return;
      }
    }
    setSubmitting(true);
    try {
      const ok = await UserProfileApi.updateProfile({
        nickname: n,
        avatarUrl: persistAv,
        gender,
        birthday: birthday ? birthday.format("YYYY-MM-DD") : "",
      });
      if (!ok) {
        msgApi.error("保存失败，请重试");
        return;
      }
      const bdayYmd = birthday ? birthday.format("YYYY-MM-DD") : undefined;
      const resolvedAvatar = persistAv
        ? resolveAppAvatarUrl(persistAv) ?? resolveAppMediaUrl(persistAv)
        : undefined;
      runInAction(() => {
        const cur = globalModel.currentUser;
        if (cur) {
          globalModel.currentUser = {
            ...cur,
            name: n,
            avatarUrl: resolvedAvatar ?? cur.avatarUrl,
            birthday: bdayYmd,
            gender,
          };
        }
        globalModel.profileSetupRequired = false;
        globalModel.profileEditorOpen = false;
      });
      void globalModel.refreshUserFromServer().then(() => {
        runInAction(() => {
          const cur = globalModel.currentUser;
          if (!cur || !resolvedAvatar) {
            return;
          }
          if (!resolveAppAvatarUrl(cur.avatarUrl) && !resolveAppMediaUrl(cur.avatarUrl)) {
            globalModel.currentUser = {
              ...cur,
              avatarUrl: resolvedAvatar,
            };
          }
        });
      });
      uploadedServerAvatarRef.current = null;
      msgApi.success("已保存");
    } finally {
      setSubmitting(false);
    }
  }, [avatarPreviewUrl, avatarUrl, birthday, gender, msgApi, nickname]);

  return (
    <Modal
      className="profile-complete-modal"
      wrapClassName="profile-complete-wrap"
      open={open}
      closable={allowDismiss}
      maskClosable={allowDismiss}
      keyboard={allowDismiss}
      onCancel={() => {
        runInAction(() => {
          globalModel.closeProfileEditor();
        });
      }}
      centered
      zIndex={3000}
      width={400}
      footer={null}
      title={null}
      destroyOnHidden
    >
      <div className="profile-complete-body">
        <h2 className="profile-complete-title">个人资料</h2>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
          className="profile-complete-file"
          onChange={onFileChange}
          tabIndex={-1}
        />
        <div className="profile-complete-avatar-block">
          <div className="profile-complete-avatar-wrap">
            <button
              type="button"
              className="profile-complete-avatar-circle"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              <EasyAvatar
                shape="circle"
                size={88}
                url={displayAvatarUrl}
                word={letter}
                alt=""
                className="profile-complete-avatar-large"
              />
              {!uploading ? (
                <span className="profile-complete-avatar-camera" aria-hidden>
                  <CameraOutlined />
                </span>
              ) : null}
            </button>
            {uploading ? (
              <div className="profile-complete-avatar-overlay" aria-hidden>
                处理中
              </div>
            ) : null}
          </div>
          {userId ? (
            <div className="profile-complete-user-id-row">
              <span className="profile-complete-user-id-value" translate="no">
                {userId}
              </span>
              <button
                type="button"
                className="profile-complete-user-id-copy"
                onClick={onCopyUserId}
                aria-label="复制用户ID"
                title="复制"
              >
                <CopyOutlined />
              </button>
            </div>
          ) : null}
        </div>
        <div className="profile-complete-field">
          <Input
            className="profile-complete-input"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            maxLength={NICKNAME_MAX_LENGTH}
            placeholder="昵称"
            showCount
            autoFocus
          />
        </div>
        <div className="profile-complete-field profile-complete-field--row">
          <div className="profile-complete-gender">
            <Radio.Group
              className="profile-complete-radio"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              optionType="button"
            >
              <Radio.Button value="U" title="未选">
                <span className="profile-complete-gender-inner">
                  <UserOutlined className="profile-complete-gi" />
                  未选
                </span>
              </Radio.Button>
              <Radio.Button value="M" title="男">
                <span className="profile-complete-gender-inner">
                  <ManOutlined className="profile-complete-gi" />
                  男
                </span>
              </Radio.Button>
              <Radio.Button value="F" title="女">
                <span className="profile-complete-gender-inner">
                  <WomanOutlined className="profile-complete-gi" />
                  女
                </span>
              </Radio.Button>
            </Radio.Group>
          </div>
        </div>
        <div className="profile-complete-field">
          <H5MobileDatePicker
            value={birthday}
            onChange={setBirthday}
            allowClear
            title="选择生日"
            placeholder="公历生日，选填"
            defaultPickerValue={BIRTHDAY_PANEL_DEFAULT}
            minDate={dayjs("1900-01-01")}
            maxDate={dayjs()}
            triggerClassName="profile-complete-input profile-complete-datepicker"
          />
        </div>
        <div
          className={
            allowDismiss
              ? "profile-complete-foot profile-complete-foot--split"
              : "profile-complete-foot"
          }
        >
          {allowDismiss ? (
            <Button
              className="profile-complete-cancel"
              disabled={submitting}
              onClick={() => {
                runInAction(() => {
                  globalModel.closeProfileEditor();
                });
              }}
            >
              取消
            </Button>
          ) : null}
          <Button
            type="primary"
            className="profile-complete-ok"
            loading={submitting}
            disabled={uploading}
            block={!allowDismiss}
            onClick={() => void onSubmit()}
          >
            确定
          </Button>
        </div>
      </div>
    </Modal>
  );
});

export default ProfileCompleteModal;
